import React, { useContext } from "react";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";
import {
  Navbar,
  SideMenu,
  DateDataTable,
  ComplaintOverview,
  ProgrammeOverview,
  FaultOverview,
  InspectOverview,
  MaintenanceOverview,
  CaseDetailsPage,
  PdfDownloadPage,
  ProgrammeDateDataTable,
  InspectionPage
} from "../../../components";
import { configContext } from "../../../components/config-provider";
import { Spin } from "../../../components/loading";
import { IApplicationState } from "../../../state/ducks";
import { AppAccessState, PAGE_ACCOUNT_MANAGEMENT } from "../../../state/ducks/access";
import { TYPE } from "../../../types";
import { AccountManagement } from "../account-management";
import { Inventory } from "../inventory";

export const Dashboard: React.FC = (props) => {
  const { disableBollard } = useContext(configContext);
  const access = useSelector<IApplicationState, AppAccessState>((state) => state.access);

  return (
    <div className="portal-layout">
      <Navbar />
      <div className="portal-layout-bottom">
        <SideMenu />
        <main>
          <div className="content-wrapper">
            <div className="content">
              <Switch>
                {/* Inventory Routes */}
                {([
                  "lamppost",
                  "lantern",
                  "bollard",
                  "controller",
                  "cable",
                  "gsds",
                  "connector",
                  "drawpit",
                ] as TYPE[]).filter((key) => !(disableBollard && key === "bollard")).map((key) => (
                  <Route
                    exact
                    key={key}
                    path={`/dashboard/inventory/${key}`}
                    component={() => <Inventory type={key} />}
                  />
                ))}

                {/* Complaint Routes */}
                {["complaint", "relationship"].map((key) => (
                  <Route
                    exact
                    key={key}
                    path={`/dashboard/complaint/${key}`}
                    component={() => (
                      <DateDataTable
                        title="complaint"
                        subtitle={key}
                        path={`complaint/${key}`}
                      />
                    )}
                  />
                ))}
                <Route
                  exact
                  path="/dashboard/complaint/case"
                  component={() => <CaseDetailsPage />}
                />

                {/* Maintenance Routes */}
                {
                  [
                    { name: 'lamppost', searchKey: 'LAMP_NO' },
                    { name: 'bollard', searchKey: 'BOLLARD_NO' },
                    { name: 'controller', searchKey: 'CONTR_NO' },
                    { name: 'cable', searchKey: 'CABLE_NO' },
                    { name: 'gsds', searchKey: 'IT_AIDS_NO' },
                  ].filter(({ name }) => !(disableBollard && name === 'bollard')).map(({ name, searchKey }) => (
                    <Route
                      exact
                      key={name}
                      path={`/dashboard/maintenance/${name}`}
                      component={() => (
                        <DateDataTable
                          title="maintenance"
                          subtitle={name}
                          path={`maintenance/${name}`}
                          monthSelector={true}
                          search={false}
                          searchKey={searchKey}
                        />
                      )}
                    />
                  ))
                }

                {/* Fault Routes */}
                {["lamppost", "bollard", "controller", "cable", "gsds"].filter((key) => !(disableBollard && key === "bollard")).map(
                  (key) => (
                    <Route
                      exact
                      key={key}
                      path={`/dashboard/fault/${key}`}
                      component={() => (
                        <DateDataTable
                          title="fault"
                          subtitle={key}
                          path={`fault/${key}`}
                        />
                      )}
                    />
                  )
                )}

                {/* Programme Routes */}
                <Route
                  exact
                  path={"/dashboard/programme/programme"}
                  component={() => (
                    <ProgrammeDateDataTable
                      title="programme"
                      subtitle="programme"
                      tablePath="programme/programme"
                      calendarPath="programme/calendar"
                    />
                  )}
                />

                <Route
                  exact
                  key="Inspection"
                  path="/dashboard/inspect/Inspection"
                  component={() => (
                    <InspectionPage
                      title="inspect"
                      subtitle="Inspection"
                      path="inspect/Inspection"
                    />
                  )}
                />

                {/* Inspect Routes */}
                {["lantern", "column"].map((key) => (
                  <Route
                    exact
                    key={key}
                    path={`/dashboard/inspect/${key}`}
                    component={() => (
                      <DateDataTable
                        title="inspect"
                        subtitle={key}
                        path={`inspect/${key}`}
                      />
                    )}
                  />
                ))}

                {/* Overview Routes */}
                <Route
                  exact
                  path="/dashboard/complaint/overview"
                  component={() => <ComplaintOverview />}
                />
                <Route
                  exact
                  path="/dashboard/programme/overview"
                  component={() => <ProgrammeOverview />}
                />
                <Route
                  exact
                  path="/dashboard/fault/overview"
                  component={() => <FaultOverview />}
                />
                <Route
                  exact
                  path="/dashboard/inspect/overview"
                  component={() => <InspectOverview />}
                />
                <Route
                  exact
                  path="/dashboard/maintenance/overview"
                  component={() => <MaintenanceOverview />}
                />
                <Route
                  exact
                  path="/dashboard/pdf/list"
                  component={() => (
                    <PdfDownloadPage
                      title="Monthly Report Download"
                      path={`pdf/list`}
                      downloadPath={`pdf/download/`}
                    />
                  )}
                />

                {/* Account management — gated on appAccess, checked here as
                    well as in the menu so a typed-in URL cannot reach it.
                    Rendered only once appAccess has loaded, otherwise a slow
                    fetch would bounce an administrator away from their own
                    page. */}
                <Route
                  exact
                  path="/dashboard/account/locked"
                  component={() =>
                    !access.loaded ? (
                      <Spin />
                    ) : access.pages.includes(PAGE_ACCOUNT_MANAGEMENT) ? (
                      <AccountManagement />
                    ) : (
                      <Redirect to="/dashboard/complaint/overview" />
                    )
                  }
                />
              </Switch>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
